<script context="module">
  import {PATTERNS} from '@/const/patterns';

  function validateEmail(value, LG, lang) {
    if (value === null || LG === null) {
      return null;
    }
    if (PATTERNS.EMAIL.test(value)) {
      return null;
    }
    if (value) {
      return LG(lang.EmailError);
    }
    return LG(lang.NoEmailError);
  }

  function validatePassword(value, LG, lang) {
    if (value === null || LG === null) {
      return null;
    }
    if (PATTERNS.PASSWORD.test(value)) {
      return null;
    }
    if (value) {
      return LG(lang.PasswordError);
    }
    return LG(lang.NoPasswordError);
  }
</script>

<script>
  import {onMount, createEventDispatcher} from 'svelte';
  import {LINKS} from '@/const/link';
  import {LANG_MAP} from '@/map';
  import {mc} from '@/core';
  import {drawerUtil} from '@/helper/drawer';
  import {navAfterRegisterSuccess} from '@/helper/login';
  import {numUtil} from '@/helper/num';
  import {getLocationSearch} from '@/helper/page';
  import {doCaptcha} from '@/captcha';
  import {ga} from '@/da/ga';
  import {sd} from '@/da/sd';
  import {userApi} from '@/api/user';
  import {lang$, fixLink$} from '@/store/lang';
  import {referral$} from '@/store/referral';
  import Button from '@/widgets/Button.svelte';
  import DrawerModal from '@/widgets/DrawerModal.svelte';
  import CreateWithGoogle from '@/modules/register/componets/CreateWithGoogle.svelte';
  import registerLang from '@/modules/components/register-form/RegisterForm.lang';
  import lang from '../RegisterLandingPage.lang';
  import ThirdPartyLogin from '@/modules/login/componets/ThirdPartyLogin.svelte';
  import lLang from '@/modules/login/LoginPage.lang';
  import SplitLine from '@/modules/login/componets/SplitLine.svelte';
  import {IP_RESTRICTED_CODE} from '@/emum';

  export let LG = null;
  export let target = null;
  export let email = null;
  export let record = null;

  const dispatch = createEventDispatcher();

  const terms = LG(registerLang.BySigningUp, {
    Term: `<a class="T1 fw3 noa" href="${$fixLink$(LINKS.PHEMEX_REFERENCES_TERMS_OF_USE, 'CMS')}" target="_blank" rel="noopener noreferrer nofollow">${LG(registerLang.Terms)}</a>`,
    Privacy: `<a class="T1 fw3 noa" href="${$fixLink$(LINKS.PHEMEX_REFERENCES_PRIVACY_POLICY, 'CMS')}" target="_blank" rel="noopener noreferrer nofollow">${LG(registerLang.PrivacyPolicy)}</a>`,
  });

  let isRegistering = false;
  let hasReferral = false;
  let password = null;
  let referralCode = '';
  let referralGroup = '';
  let errorEmailMsg = '';
  let errorPasswordMsg = '';
  let isSeePassword = false;
  let isTerm = true;
  let isSeeCode = false;

  $: errorPasswordMsg = validatePassword(password, LG, lang);
  $: updateCode(record);
  $: updateSeeCode(referralCode);

  onMount(() => sd.track('side_popup_open', {ui_position: 'register_popup'}));

  onMount(() => {
    const {referralCode, group} = getLocationSearch();
    referralCode && referral$.set({code: referralCode, group});
    group && numUtil.isNumber(group) && userApi.addGroupClickCnt(group);
  });

  onMount(() =>
    referral$.subscribe(({code, group}) => {
      hasReferral = !!code;
      referralCode = code;
      referralGroup = group;
    })
  );

  function updateCode(record) {
    if (record) {
      const {referralCode, group} = record;
      referralCode && referral$.set({code: referralCode, group});
      group && numUtil.isNumber(group) && userApi.addGroupClickCnt(group);
    }
  }

  function updateSeeCode(referralCode) {
    if (referralCode) {
      isSeeCode = true;
    }
  }

  function handleClickClose() {
    dispatch('close');
    resetData();
  }

  function handleEnterKey(e) {
    if (e.keyCode === 13) {
      handleButtonContinueClick();
    }
  }

  async function handleButtonContinueClick() {
    sd.track('launch_register', {referral_code: referralCode, ui_position: 'register_popup'});
    email = email || '';
    password = password || '';
    errorEmailMsg = validateEmail(email, LG, lang);
    if (email && password && errorEmailMsg === null && errorPasswordMsg === null) {
      isRegistering = true;
      const captchaResult = await doCaptcha('register');
      if (!captchaResult) {
        mc.send('toast', 'error', {message: LG(lang.NetworkError)});
        isRegistering = false;
        return;
      }

      const params = {
        email,
        password,
        referralCode,
        group: !!referralGroup ? Number(referralGroup) : 0,
        nickName: '',
        lang: LANG_MAP[$lang$] || $lang$,
      };
      const {data, error} = await userApi.signUp(captchaResult, params);
      isRegistering = false;
      if (data) {
        if (data.status === '1') {
          errorEmailMsg = LG(lang.TheAccountHasBeen);
          return;
        }
        dispatch('close');
        drawerUtil.launchEmailVerification(LG, {
          params,
          type: 'mail-registration-code',
          context: data,
          async callback(mailCode, callback, context) {
            const code = context.code;
            const email = context.email;
            const data = await confirmLoginSure(code, mailCode, email);
            callback(data);
          },
        });
      }
      if (error) {
        if ([IP_RESTRICTED_CODE.WARNING, IP_RESTRICTED_CODE.RESTRICTED].includes(error.code)) {
          drawerUtil.launchRegisterTips(LG);
          return;
        }
        mc.send('toast', 'error', error);
      }
    }
  }

  async function confirmLoginSure(code, mailCode, email) {
    const query = {
      code,
      mailCode,
      email,
    };
    const {data, error} = await userApi.confirmRegister(query);
    if (data) {
      localStorage.setItem('close-receive-member', 'N');
      localStorage.setItem('base-kyc-status', '1');
      mc.send('toast', 'info', {message: LG(lang.Registered)});
      await ga.bindGoogleClientId();
      navAfterRegisterSuccess(target);
      return {code: 0, msg: ''};
    }
    if (error) {
      mc.send('toast', 'error', error);
      return {code: error.code, msg: error.message};
    }
  }

  function handleSeePasswordClick() {
    isSeePassword = !isSeePassword;
  }

  function handleClickLogin() {
    drawerUtil.launchLogin(LG, target);
    handleClickClose();
  }

  function resetData() {
    isRegistering = false;
    hasReferral = false;
    email = null;
    password = null;
    referralCode = '';
    referralGroup = '';
    errorEmailMsg = '';
    errorPasswordMsg = '';
  }

  function handleFocusFun(type) {
    return function () {
      sd.track('input_box_focus', {input_box_type: type, business_type: 'register', ui_position: 'register_popup'});
    };
  }

  function handleBlurFun(type) {
    return function () {
      errorEmailMsg = validateEmail(email, LG, lang);
      sd.track('input_box_unfocus', {input_box_type: type, business_type: 'register', ui_position: 'register_popup'});
    };
  }
</script>

<style>
  .register-wrap {
    height: 100%;
  }
  .close-btn:hover .iconfont {
    color: var(--T2);
  }
  .title {
    margin-top: 64px;
    line-height: 48px;
    font-weight: var(--fw3);
    font-size: 32px;
    color: var(--T1);
  }

  .ipt-box {
    border: 1px solid #dbdfe7;
    border-radius: 8px;
    padding: 0 20px;
    height: 48px;
  }
  .ipt-box.error {
    border: 1px solid #f66;
  }
  .ipt-box:focus-within,
  .ipt-box:hover {
    border: 1px solid var(--blue-main);
  }
  .ipt {
    border: none;
    height: 90%;
    outline: none;
  }
  .iconfont {
    margin-top: 2px;
  }
  .login-row {
    margin-top: 20px;
  }
  .disabled {
    background: #efefef;
  }
  .wrap :global(input.code-ipt[disabled]) {
    color: var(--T2);
    opacity: 1;
  }

  .c6 {
    color: #6c6e74;
  }
  .c-grey{
    color: #6C6E74;
  }
  .register-wrap .ipt-box,
  .register-wrap :global(.br48){
    border-radius: 48px;
  }
  .form .error-msg{
    bottom: -17px;
    left: 0;
  }
  .form .rotate-bottom{
    transform: rotate(180deg);
  }
  .btn :global(button.primary) {
    background: var(--BTN-BG);
    color: var(--BTN-COLOR);
    border-color: var(--BTN-BG);
  }
</style>

<DrawerModal visible>
  <div class="register-wrap pr">
    <div class="close-btn tr cp" on:click={handleClickClose}><i class="iconfont f30 T4">&#xe614;</i></div>
    <div class="form">
      <div class="title df aic f32 lh48 fw3">{LG(registerLang.CreateAccount)}</div>
      <div class="f12 lh16 mt24 pl4 c-grey">{LG(lang.Email)}</div>
      <div class="ipt-box df aic mt4 br48 pr" class:disabled={isRegistering} class:error={errorEmailMsg}>
        <input
          class="autofill ipt f1 "
          type="text"
          placeholder={LG(lang.Email)}
          bind:value={email}
          on:keypress={handleEnterKey}
          disabled={isRegistering}
          on:focus={handleFocusFun('email_input_box')}
          on:blur={handleBlurFun('email_input_box')}
        />
        {#if errorEmailMsg}
          <div class="sell f12 lh16 pa error-msg">{errorEmailMsg}</div>
        {/if}
      </div>
      <div class='f12 lh16 mt20 pl4 c-grey'>{LG(lang.Password)}</div>
      <div class="pr">
        <div class="ipt-box df aic mt4 br48" class:disabled={isRegistering} class:error={errorPasswordMsg}>
          {#if isSeePassword}
            <input
              class="autofill ipt f1"
              type="text"
              placeholder={LG(lang.Password)}
              disabled={isRegistering}
              bind:value={password}
              on:keypress={handleEnterKey}
              on:focus={handleFocusFun('password_input_box')}
              on:blur={handleBlurFun('password_input_box')}
            />
          {:else}
            <input
              class="autofill ipt f1 br48"
              type="password"
              placeholder={LG(lang.Password)}
              disabled={isRegistering}
              bind:value={password}
              on:keypress={handleEnterKey}
              on:focus={handleFocusFun('password_input_box')}
              on:blur={handleBlurFun('password_input_box')}
            />
          {/if}
          <i class="iconfont T4 cp" on:click={handleSeePasswordClick}>{@html !isSeePassword ? '&#xe73f;' : '&#xe750;'}</i>
        </div>
        {#if errorPasswordMsg}
          <div class="mt2 sell f12 ml4 lh16">{errorPasswordMsg}</div>
        {/if}
      </div>
      <div class="code mt20">
        <div class="df aic lh16 f12 ml4 cp c-grey home-register-invite-button" on:click={() => (isSeeCode = !isSeeCode)}>
          {LG(lang.IndividualCode)}<img width="12" height="12" class="ml4" class:rotate-bottom={isSeeCode} src="https://img.phemex.com/v1/e325b902-a9bf-430d-a8b0-cbc7aeaa38df/short.svg" />
        </div>
        {#if isSeeCode}
          <div class="ipt-box df aic mt20" class:disabled={hasReferral}>
            <input class="ipt code-ipt f1 br48" type="text" placeholder={LG(lang.IndividualCode)} maxlength="32" disabled={hasReferral} bind:value={referralCode} on:keypress={handleEnterKey} />
          </div>
        {/if}
      </div>
      <div class="term-link df mt20 c6">
        <i class="iconfont mr4 f14 cp {isTerm ? 'T1' : 'T4'}" on:click={() => (isTerm = !isTerm)}>{@html isTerm ? '&#xe61f;' : '&#xe6d3;'}</i>
        <div class="f12 c-grey">{@html terms}</div>
      </div>
      <div class="btn mt20 ga" data-gac="user.landingpage" data-gaa="link_click" data-gal="landingpage.side_continue">
        <Button class="br48" minWidth="100%" type="primary" loading={isRegistering} disabled={!!errorEmailMsg || !!errorPasswordMsg || !isTerm} on:click={handleButtonContinueClick}>{LG(lang.Continue)}</Button>
      </div>
      <div class="login-row f12 lh16"><span class="c-grey">{LG(lang.AlreadyHaveAnAccount)}</span> <a class="T1 fw3 noa cp" href="javascript:void(0);" on:click={handleClickLogin}>{LG(lang.LogIn)}</a></div>
      <SplitLine text={LG(lLang.OrContinueWith)}/>
      <ThirdPartyLogin {target} {referralCode} entrance='Register'/>
    </div>
  </div>
</DrawerModal>
